import { WIDGET_KEYS } from "../WidgetKeys.jsx";
import { TASK_BUDGET_FIELDS } from "../OtherKeys.jsx";
import { lineThroughRenderer, swivuGridSorterFn as sortable } from "../../utils/utils.jsx";
import {
  budgetColRenderer,
  budgetCurrencyFormat,
  budgetSummaryRenderer,
  disabledFieldsConfig
} from "../../utils/budgetUtils.jsx";
import { ColumnStore } from "@bryntum/schedulerpro";

export const BUDGET_VIEW_COLUMNS = [
  {
    id: "task-budget-id",
    type: WIDGET_KEYS.columnTaskBudgetText,
    editor: false,
    text: "Budget ID",
    region: "locked",
    field: TASK_BUDGET_FIELDS.title,
    renderer({ record }) {
      if (!record.isGroupHeader) {
        return lineThroughRenderer(record[TASK_BUDGET_FIELDS.title] || "N/A", record.archived)
      }
    }
  },
  // We want budget annotation here from `columnTaskBudget` as it is a number column therefore applying sorter explicitly on this column
  {
    id: "task-budget-description",
    type: WIDGET_KEYS.columnTaskBudgetText,
    text: "Description",
    region: "locked",
    field: TASK_BUDGET_FIELDS.description,
    sortable,
    summaryRenderer: () => {
      return `<div class="swivu-budget-summary-container">
      <input type="checkbox" id="swivu-budget-summary-checkbox"/>
      <label for="swivu-budget-summary-checkbox">Sum Selected Rows</label>
    </div>`;
    }
  },
  {
    id: "task-budget-contract-group",
    text: "Contract",
    type: WIDGET_KEYS.collapsibleColumn,
    children: [
      { id: "task-budget-contract-amount", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Contract Amount", field: TASK_BUDGET_FIELDS.contractAmount },
      { id: "task-budget-change-orders", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Change Orders", field: TASK_BUDGET_FIELDS.changeOrders },
      { id: "task-budget-total-contract", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Total Contract", field: TASK_BUDGET_FIELDS.totalContract, editor: false, ...disabledFieldsConfig },
    ]
  },
  {
    id: "task-budget-summary-group",
    text: "Summary",
    type: WIDGET_KEYS.collapsibleColumn,
    children: [
      { id: "task-budget-total-costs-estimated", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Total Costs Estimated", field: TASK_BUDGET_FIELDS.totalCostEstimated, editor: false, ...disabledFieldsConfig },
      { id: "task-budget-gross-profit-estimated", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Gross Profit Estimated", field: TASK_BUDGET_FIELDS.grossProfitEstimated, editor: false, ...disabledFieldsConfig },
      { id: "task-budget-gross-margin-estimated", type: WIDGET_KEYS.columnTaskBudgetPercentage, text: "Gross Margin Estimated", field: TASK_BUDGET_FIELDS.grossMarginEstimated, editor: false, ...disabledFieldsConfig },
      { id: "task-budget-total-costs-actual", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Total Costs Actual", field: TASK_BUDGET_FIELDS.totalCostActual, editor: false, ...disabledFieldsConfig },
      { id: "task-budget-gross-profit-actual", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Gross Profit Actual", field: TASK_BUDGET_FIELDS.grossProfitActual, editor: false, ...disabledFieldsConfig },
      { id: "task-budget-gross-margin-actual", type: WIDGET_KEYS.columnTaskBudgetPercentage, text: "Gross Margin Actual", field: TASK_BUDGET_FIELDS.grossMarginActual, editor: false, ...disabledFieldsConfig },
    ]
  },
  {
    id: "task-budget-estimates-group",
    text: "Estimates",
    type: WIDGET_KEYS.collapsibleColumn,
    children: [
      { id: "task-budget-labor-estimated", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Labor Estimated" , field: TASK_BUDGET_FIELDS.laborEstimated },
      { id: "task-budget-materials-estimated", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Materials Estimated" , field: TASK_BUDGET_FIELDS.materialsEstimated },
      { id: "task-budget-subs-estimated", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Subs Estimated" , field: TASK_BUDGET_FIELDS.subsEstimated },
      { id: "task-budget-rentals-estimated", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Rentals Estimated" , field: TASK_BUDGET_FIELDS.rentalsEstimated },
      { id: "task-budget-miscellaneous-estimated", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Miscellaneous Estimated" , field: TASK_BUDGET_FIELDS.miscellaneousEstimated },
      { id: "task-budget-days-estimated", type: WIDGET_KEYS.columnTaskBudget, text: "Days Estimated", field: TASK_BUDGET_FIELDS.daysEstimated, sum: "sum", summaryRenderer: budgetSummaryRenderer() },
    ]
  },
  {
    id: "task-budget-actual-group",
    text: "Actual",
    type: WIDGET_KEYS.collapsibleColumn,
    children: [
      { id: "task-budget-labor-actual", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Labor Actual" , field: TASK_BUDGET_FIELDS.laborActual },
      { id: "task-budget-materials-actual", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Materials Actual" , field: TASK_BUDGET_FIELDS.materialsActual },
      { id: "task-budget-subs-actual", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Subs Actual" , field: TASK_BUDGET_FIELDS.subsActual },
      { id: "task-budget-rentals-actual", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Rentals Actual" , field: TASK_BUDGET_FIELDS.rentalsActual },
      { id: "task-budget-miscellaneous-actual", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Miscellaneous Actual" , field: TASK_BUDGET_FIELDS.miscellaneousActual },
      { id: "task-budget-days-actual", type: WIDGET_KEYS.columnTaskBudget, text: "Days Actual", field: TASK_BUDGET_FIELDS.daysActual, sum: "sum", summaryRenderer: budgetSummaryRenderer(), editor: false, ...disabledFieldsConfig },
    ]
  },
  {
    id: "task-budget-variances-group",
    text: "Variances",
    type: WIDGET_KEYS.collapsibleColumn,
    children: [
      { id: "task-budget-labor-variance", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Labor Variance" , field: TASK_BUDGET_FIELDS.laborVariance, summaryRenderer: budgetSummaryRenderer(budgetCurrencyFormat, "", true), editor: false,
        ...disabledFieldsConfig
       },
      { id: "task-budget-materials-variance", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Materials Variance" , field: TASK_BUDGET_FIELDS.materialsVariance, summaryRenderer: budgetSummaryRenderer(budgetCurrencyFormat, "", true), editor: false,
        ...disabledFieldsConfig
       },
      { id: "task-budget-subs-variance", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Subs Variance" , field: TASK_BUDGET_FIELDS.subsVariance, summaryRenderer: budgetSummaryRenderer(budgetCurrencyFormat, "", true), editor: false,
        ...disabledFieldsConfig
       },
      { id: "task-budget-rentals-variance", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Rentals Variance" , field:TASK_BUDGET_FIELDS.rentalsVariance, summaryRenderer: budgetSummaryRenderer(budgetCurrencyFormat, "", true), editor: false,
        ...disabledFieldsConfig
       },
      { id: "task-budget-miscellaneous-variance", type: WIDGET_KEYS.columnTaskBudgetAmount, text: "Miscellaneous Variance" , field: TASK_BUDGET_FIELDS.miscellaneousVariance, summaryRenderer: budgetSummaryRenderer(budgetCurrencyFormat, "", true), editor: false,
        ...disabledFieldsConfig
       },
      {
        id: "task-budget-days-variance",
        type: WIDGET_KEYS.columnTaskBudget,
        text: "Days Variance",
        field: TASK_BUDGET_FIELDS.daysVariance,
        renderer(e) {
          if (!e.record.isGroupHeader) {
            return budgetColRenderer(e, true);
          }
        },
        sum: "sum",
        summaryRenderer: budgetSummaryRenderer(null, "", true),
        editor: false,
        ...disabledFieldsConfig
      },
    ]
  }
];

export const TASK_BUDGET_VIEW_FILTER_FIELD_CONFIG = {
  budgetTasksValues: { type: '_swivu_settings' },
  // Budget fields are also generated, so we should provide field type for them
  ...Object.values(TASK_BUDGET_FIELDS).reduce((previousBudgetFields, budgetField) => ({
    ...previousBudgetFields,
    [budgetField]: {type: "number"}
  }), {}),
  // Most are numbers, except for these two.
  [TASK_BUDGET_FIELDS.title]: { type: 'string' },
  [TASK_BUDGET_FIELDS.description]: { type: 'string' }
};

const EXCLUDED_HOVER_FIELDS = [TASK_BUDGET_FIELDS.description];
const getBudetFieldConfig = (column) => {
  const { field, text, type } = column;
  if (!EXCLUDED_HOVER_FIELDS.includes(field)) {
    const columnDefaultConfig = ColumnStore.getColumnClass(type)?.defaults;
    if (columnDefaultConfig) {
      return ({
        budgetField: field,
        budgetFieldTitle: text,
        budgetValueFormatter: columnDefaultConfig.format,
        isPercentage: type === WIDGET_KEYS.columnTaskBudgetPercentage
      });
    }
  }
}
export const BUDGET_FIELDS_CONFIG = BUDGET_VIEW_COLUMNS.reduce((prevColumns, column) => {
  if (column.children?.length) {
    column.children.forEach((budgetColumn) => {
      const config = getBudetFieldConfig(budgetColumn);
      if (config) prevColumns.push(config);
    })
  } else {
    const config = getBudetFieldConfig(column);
    if (config) prevColumns.push(config);
  }
  return prevColumns;
}, []);
